import React from 'react';
import { Box, Typography } from '@mui/material';
import { season } from './format';
import {
  ASKED_LABEL, C, MONO, RADIUS, SEASON_COLOUR, SEASON_LABEL, SHADOW, ZONE_NUMERALS, swatchHex,
  swatchNeedsRule,
} from './tokens';
import { SeasonKey } from './types';

// One product photo often covers five colourways, so the colour word carries a dot of that colour.
// No dot for a pattern or a colour we cannot name — a wrong dot is worse than none.
export const ColorDot: React.FC<{ name?: string | null; size?: number }> = ({ name, size = 12 }) => {
  const hex = swatchHex(name);
  if (!hex) return null;
  return (
    <Box
      component="span" aria-hidden="true"
      sx={{
        display: 'inline-block', width: size, height: size, borderRadius: '50%', flexShrink: 0,
        bgcolor: hex, mr: '5px', verticalAlign: 'middle',
        border: '1px solid', borderColor: swatchNeedsRule(hex) ? C.line2 : 'rgba(25,22,17,.16)',
      }}
    />
  );
};

// 秋 / 夏 on every card and tile, so a part-timer can see the rail flipping season.
export const SeasonBadge: React.FC<{ value?: SeasonKey | null; size?: 'sm' | 'xs' }> = ({ value, size = 'sm' }) => {
  const key = season(value);
  if (!key || key === 'ALL') return null;
  const tone = SEASON_COLOUR[key];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', fontSize: size === 'xs' ? 9.5 : 10.5, fontWeight: 800,
        lineHeight: 1.5, px: size === 'xs' ? '4px' : '6px', borderRadius: '99px',
        color: tone.colour, bgcolor: tone.wash, border: '1px solid', borderColor: tone.colour,
        whiteSpace: 'nowrap',
      }}
    >
      {SEASON_LABEL[key]}
    </Box>
  );
};

// Design A (2026-10-05): 熱門款 / 溫款, with the store split when it is known — 「熱門款 · 上架 14＋櫃位 10」.
export const HEAT_LABEL = { hot: '熱門款', warm: '溫款' } as const;
export const HeatBadge: React.FC<{
  level?: 'hot' | 'warm' | null; railFace?: number | null; storage?: number | null;
}> = ({ level, railFace, storage }) => {
  if (level !== 'hot' && level !== 'warm') return null;
  const split = typeof railFace === 'number' && typeof storage === 'number' ? ` · 上架 ${railFace}＋櫃位 ${storage}` : '';
  return (
    <Box
      component="span" data-heat={level}
      sx={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 800, lineHeight: 1.5, px: '6px',
        borderRadius: '99px', whiteSpace: 'nowrap', border: '1px solid', borderColor: C.back,
        color: level === 'hot' ? '#fff' : C.back, bgcolor: level === 'hot' ? C.back : C.backWash,
      }}
    >
      {HEAT_LABEL[level]}{split}
    </Box>
  );
};

// Design A item 6: a HOT style's Korea pieces are received straight into the store (櫃位).
export const DIRECT_STORE_LABEL = '到貨直送門市';
export const DirectStoreBadge: React.FC<{ on?: boolean | null }> = ({ on }) => {
  if (!on) return null;
  return (
    <Box
      component="span" data-direct-store="1"
      sx={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 800, lineHeight: 1.5, px: '6px',
        borderRadius: '99px', whiteSpace: 'nowrap', color: C.fetch, bgcolor: C.fetchWash,
        border: '1px solid', borderColor: C.fetch,
      }}
    >
      {DIRECT_STORE_LABEL}
    </Box>
  );
};

// Design A item 7: 線上上升, or 重新考慮 when he once rejected it.
export const RisingBadge: React.FC<{ reconsider?: boolean | null }> = ({ reconsider }) => (
  <Box
    component="span" data-rising={reconsider ? 'reconsider' : 'rising'}
    sx={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 800, lineHeight: 1.5, px: '6px', mb: '3px',
      borderRadius: '99px', whiteSpace: 'nowrap', color: C.go, bgcolor: C.goWash,
      border: '1px solid', borderColor: C.go,
    }}
  >
    {reconsider ? '重新考慮・線上上升' : '線上上升'}
  </Box>
);

// R5: this row exists because a customer walked in and asked for it.
export const AskedBadge: React.FC<{ asked?: boolean | null }> = ({ asked }) => {
  if (!asked) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 800, lineHeight: 1.5, px: '6px',
        borderRadius: '99px', color: '#fff', bgcolor: C.back, whiteSpace: 'nowrap',
      }}
    >
      {ASKED_LABEL}
    </Box>
  );
};

export type ChipTone = 'plain' | 'place' | 'wait' | 'warn';

const CHIP_TONE: Record<ChipTone, { bg: string; fg: string }> = {
  plain: { bg: C.paper2, fg: C.ink2 },
  place: { bg: C.ink, fg: '#F3EDE1' },
  wait: { bg: C.waitWash, fg: C.wait },
  warn: { bg: C.backWash, fg: C.back },
};

export const Chip: React.FC<{ tone?: ChipTone; children: React.ReactNode }> = ({ tone = 'plain', children }) => {
  const colours = CHIP_TONE[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: 11.5, fontWeight: 700,
        px: '7px', py: '2px', borderRadius: '99px', bgcolor: colours.bg, color: colours.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

// ①..⑥ — the walk number, the one thing that ties the map to the 今天 list. `mark` replaces the
// numeral for the two quarantine sections, which are not stops on the walk.
export const ZoneNumeral: React.FC<{ index?: number; mark?: string; bg?: string; size?: number }> = ({
  index = 0, mark, bg = C.ink, size = 10,
}) => (
  <Box
    component="span"
    sx={{
      fontFamily: MONO, fontSize: size, fontWeight: 700, color: '#fff', bgcolor: bg,
      borderRadius: '4px', px: '4px', lineHeight: 1.35, flex: 'none',
    }}
  >
    {mark || ZONE_NUMERALS[index] || String(index + 1)}
  </Box>
);

export interface StatCell {
  label: string;
  value: React.ReactNode;
  suffix?: string;
  highlight?: boolean;
}

// The 3–4 numbers at the top of a screen, in one paper strip.
export const StatStrip: React.FC<{ cells: StatCell[] }> = ({ cells }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'stretch', bgcolor: C.card, border: '1px solid', borderColor: C.line,
      borderRadius: `${RADIUS}px`, boxShadow: SHADOW, overflow: 'hidden', mb: 1.5,
    }}
  >
    {cells.map((cell, index) => (
      <Box
        key={cell.label}
        aria-label={cell.label}
        sx={{
          flex: 1, px: { xs: 0.75, sm: 1.25 }, py: 1, minWidth: 0,
          borderRight: index === cells.length - 1 ? 0 : '1px solid', borderColor: C.line,
        }}
      >
        <Typography
          component="div"
          sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: C.ink3, lineHeight: 1.4 }}
        >
          {cell.label}
        </Typography>
        {/* nowrap + a smaller step at phone width: 「14／150」 used to orphan its 件 onto line 2. */}
        <Typography
          component="div"
          sx={{
            fontFamily: MONO, fontWeight: 700, fontSize: { xs: 15.5, sm: 19 }, letterSpacing: '-.02em',
            lineHeight: 1.25, color: cell.highlight ? C.back : C.ink,
            whiteSpace: 'nowrap', overflowWrap: 'normal', wordBreak: 'keep-all',
          }}
        >
          {cell.value}
          {cell.suffix && (
            <Box
              component="span"
              sx={{ fontSize: { xs: 10, sm: 11 }, color: C.ink3, fontWeight: 500, ml: '2px', whiteSpace: 'nowrap' }}
            >
              {cell.suffix}
            </Box>
          )}
        </Typography>
      </Box>
    ))}
  </Box>
);

// The small all-caps rule above a block, design B's 「這個架子應該長這樣」 heading.
export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h3"
    sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: C.ink3, mt: 2.25, mb: 1, mx: '2px' }}
  >
    {children}
  </Typography>
);
